import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { Door, Location, VisitorGroup } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { getLockGroupsThatLockBelongTo, getVisitorGroupsWithAccessToDevice } from 'utils/Lock/Lock';
import BatteryStatusCell from 'v2/components/BatteryStatusCell/BatteryStatusCell';
import { ColumnHeaderWithTooltip } from 'v2/components/ColumnHeaderWithTooltip/ColumnHeaderWithTooltip';
import { getOverviewDataGridProps } from 'v2/components/DataGrid/getOverviewDataGridProps';
import { renderHeaderWithId } from 'v2/components/DataGrid/renderHeaderWithId';
import { DeviceStatusColumnHeader } from 'v2/components/DeviceStatusColumnHeader/DeviceStatusColumnHeader';
import { DevicesAndUsersGroupsCell } from 'v2/components/DevicesAndUsersGroupsCell/DevicesAndUsersGroupsCell';
import { GroupsColumnHeader } from 'v2/components/GroupsColumnHeader/GroupsColumnHeader';
import LockIconNameCell from 'v2/components/LockIconNameCell/LockIconNameCell';
import { TextCell } from 'v2/components/TextCell/TextCell';
import { DATAGRID_ROW_HEIGHT } from 'v2/constants';
import { getOfficeModeDisplayText } from 'v2/utils/lock';

interface Props {
  devices: Door[];
  lockGroups: Location[];
  visitorGroups: VisitorGroup[];
  onRowClick: (key: string) => void;
  loading?: boolean;
}

const TableWithData: React.FC<Props> = ({ devices, lockGroups, visitorGroups, onRowClick, loading = false }) => {
  const { translation } = useTranslation();
  const theme = useTheme();

  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const headerWithId = renderHeaderWithId();

  const columns: GridColDef<Door>[] = [
    {
      field: 'name',
      headerName: translation.name,
      flex: 1,
      renderHeader: headerWithId,
      renderCell: (params) => <LockIconNameCell door={params.row} />
    },
    {
      field: 'groups',
      headerName: translation.groups,
      flex: 1,
      sortable: false,
      renderHeader: renderHeaderWithId(undefined, () => <GroupsColumnHeader />),
      renderCell: (params) => {
        const deviceGroups = getLockGroupsThatLockBelongTo(lockGroups, params.row.id);
        const userGroupsCount = getVisitorGroupsWithAccessToDevice(params.row.id, deviceGroups, visitorGroups).length;
        return <DevicesAndUsersGroupsCell deviceGroupsCount={deviceGroups.length} userGroupsCount={userGroupsCount} />;
      }
    },
    {
      field: 'office_mode',
      headerName: translation.office_mode,
      flex: 1,
      minWidth: 160,
      renderHeader: () => (
        <ColumnHeaderWithTooltip
          id="header-office_mode"
          label={translation.office_mode}
          tooltip={<Typography variant="caption">{translation.device_office_mode_tooltip}</Typography>}
        />
      ),
      valueGetter: (_, row) => getOfficeModeDisplayText(row, translation),
      renderCell: (params) => <TextCell>{getOfficeModeDisplayText(params.row, translation)}</TextCell>
    },
    {
      field: 'serial_number',
      headerName: translation.serial_number,
      flex: 1,
      minWidth: 170,
      valueGetter: (_, row) => row.hardwareIdentifier,
      renderHeader: () => (
        <ColumnHeaderWithTooltip
          id="header-serial_number"
          label={translation.serial_number}
          tooltip={<Typography variant="caption">{translation.serial_number_tooltip}</Typography>}
        />
      ),
      renderCell: (params) => <TextCell>{params.row.hardwareIdentifier}</TextCell>
    },
    {
      field: 'status',
      headerName: translation.status,
      sortable: true,
      resizable: false,
      width: isMdUp ? 140 : 120,
      renderHeader: () => <DeviceStatusColumnHeader />,
      valueGetter: (_, row) => row.batteryWarningLevel,
      renderCell: (params) => <BatteryStatusCell level={params.row.batteryWarningLevel} iconOnly={!isMdUp} />
    }
  ];

  const columnVisibilityModel = {
    name: true,
    groups: isSmUp,
    office_mode: isSmUp,
    serial_number: isLgUp,
    status: true
  };

  const overviewDataGridProps = getOverviewDataGridProps({ rowCount: devices.length, loading });

  return (
    <DataGrid
      {...overviewDataGridProps}
      rows={devices}
      columns={columns}
      columnVisibilityModel={columnVisibilityModel}
      rowHeight={DATAGRID_ROW_HEIGHT}
      onRowClick={(params: GridRowParams<Door>) => onRowClick(params.row.id)}
      initialState={{
        ...overviewDataGridProps.initialState,
        sorting: { sortModel: [{ field: 'name', sort: 'asc' }] }
      }}
    />
  );
};

export default TableWithData;
